import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useForgotPasswordMutation } from '@/hooks/useAuth'
import { useState } from 'react'
import { toast } from 'sonner'
import { Link } from 'react-router-dom'

const schema = z.object({
  email: z.email('Email invalide'),
})

type FormValues = z.infer<typeof schema>

export default function ForgotPassword() {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  })
  const { mutateAsync, isPending } = useForgotPasswordMutation()
  const [devToken, setDevToken] = useState<string | null>(null)

  async function onSubmit(values: FormValues) {
    try {
      const res = await mutateAsync({
        email: values.email.trim().toLowerCase(),
      })
      // Réponse back: { ok: true, token?: string } (token en dev only)
      setDevToken(res?.token ?? null)
      toast.success(
        'Si un compte existe, un email de réinitialisation a été envoyé.'
      )
    } catch (e: any) {
      // Même message (pas de leak) + description utile si dev
      toast.success(
        'Si un compte existe, un email de réinitialisation a été envoyé.'
      )
    }
  }

  return (
    <div className='flex min-h-[100vh] items-center justify-center bg-gradient-to-br from-[hsl(0,0%,100%)] via-[hsl(140,13%,91%)] to-[hsl(144,80%,73%)]'>
      <Card className='w-[520px] shadow-2xl rounded-xl'>
        <CardHeader>
          <h1 className='text-2xl font-semibold'>Mot de passe oublié</h1>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
              <FormField
                control={form.control}
                name='email'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        placeholder='Votre email'
                        inputMode='email'
                        autoComplete='email'
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type='submit' disabled={isPending}>
                {isPending ? 'Envoi…' : 'Envoyer le lien'}
              </Button>
            </form>
          </Form>

          {devToken && (
            <div className='mt-6 rounded-md border p-3 text-sm'>
              <div className='font-medium mb-1'>Dev only — Token :</div>
              <code className='block truncate'>{devToken}</code>
              <Link
                to={`/reset-password?token=${encodeURIComponent(devToken)}`}
                className='text-primary underline'
              >
                Ouvrir la page de réinitialisation
              </Link>
            </div>
          )}
        </CardContent>

        <CardFooter className='justify-center text-sm text-muted-foreground'>
          <Link to='/login' className='text-primary hover:underline'>
            Retour à la connexion
          </Link>
        </CardFooter>
      </Card>
    </div>
  )
}
